import { Stack, Text } from '@chakra-ui/react'
import type { EditOrganizationUnit } from 'BrokerTsLib/src/dtos'
import { Insurer, OrganizationUnitType, Product } from 'BrokerTsLib/src/enums'
import { useGetAvailableInsurers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import type { Ref } from 'react'
import { forwardRef } from 'react'

import type { FormSectionProps } from '@/containers/forms/FormSection'
import { FormSection } from '@/containers/forms/FormSection'
import { usePartner } from '@/context/PartnerContext'
import { useAvailableProducts } from '@/hooks/useProducts'
import { InputFormControl } from 'BrokerTsLib/src/components'

export interface OrganizationLoginSettingsFormSectionProps extends FormSectionProps {
  ouId: number
}

const PropertyInsurers = [
  [Insurer.Colonnade, 'colonnadeCredentials'],
  [Insurer.Colonnade, 'colonnadeTravelCredentials'],
  [Insurer.Pillow, 'pillowCredentials'],
  [Insurer.Csob, 'csobCredentials'],
  [Insurer.Axa, 'axaAssistanceCredentials'],
  [Insurer.PVZP, 'pVzpCredentials'],
  [Insurer.SV, 'svBrokerToken'],
]

export const OrganizationLoginSettingsFormSection = forwardRef(
  function OrganizationLoginSettingsFormSection({ ouId, ...props }: OrganizationLoginSettingsFormSectionProps, ref: Ref<HTMLDivElement>) {
    const { currentPartner } = usePartner()
    const { control, watch } = useFormContext<EditOrganizationUnit>()
    const products = useAvailableProducts()

    const { data: insurerData } = useGetAvailableInsurers(ouId)
    const availableInsurers = insurerData?.data.map(x => x.insurerId as Insurer) ?? []

    const ouType = watch('type')
    const showGcpPartnerSettings = currentPartner?.partner === 'Kapitol' && ouType === OrganizationUnitType.GcpPartner

    const isVisible = (property: string) => {
      return availableInsurers.some(insurer => PropertyInsurers.some(pi => pi[0] === insurer && pi[1] === property))
    }

    const anySettings = availableInsurers.some(insurer => PropertyInsurers.some(pi => pi[0] === insurer)) || showGcpPartnerSettings

    return <FormSection ref={ref} sectionTitle='Nastavení loginů a hesel' {...props}>
      <Stack gap={6}>
        {!anySettings && <Text>Žádné nastavení loginů a hesel pro dostupné pojišťovny.</Text>}

        {isVisible('csobCredentials') &&
          <InputFormControl label='ČSOB' control={control} name='properties.csobCredentials' placeholder='login|heslo' pattern='.+\|.+' />
        }

        {isVisible('pillowCredentials') &&
          <InputFormControl label='Pillow' control={control} name='properties.pillowCredentials' placeholder='login|heslo' pattern='.+\|.+' />
        }

        {isVisible('pVzpCredentials') &&
          <InputFormControl label='Pojišťovna VZP' control={control} name='properties.pVzpCredentials' placeholder='login|heslo' pattern='.+\|.+' />
        }

        {isVisible('axaAssistanceCredentials') &&+
        <InputFormControl label='Axa Assistance' control={control} name='properties.axaAssistanceCredentials' placeholder='login|heslo' pattern='.+\|.+' />
        }

        {isVisible('colonnadeCredentials') &&
          <InputFormControl label='Colonnade' control={control} name='properties.colonnadeCredentials' placeholder='login|heslo' pattern='.+\|.+' />
        }

        {isVisible('colonnadeTravelCredentials') && products.includes(Product.TravelInsurance) &&
          <InputFormControl label='Colonnade (Cestovní pojištění)' control={control} name='properties.colonnadeTravelCredentials' placeholder='login|heslo|získatel Colonnade' pattern='.+\|.+\|.+' />
        }

        {isVisible('svBrokerToken') &&
          <InputFormControl label='SV pojišťovna - token makléře' control={control} name='properties.svBrokerToken' placeholder='token' />
        }

        {showGcpPartnerSettings && <>
          <InputFormControl label='GČP WS Username' control={control} name='properties.gcpWs1Username' />
          <InputFormControl label='GČP WS Password' control={control} name='properties.gcpWs1Pwd' />
          <InputFormControl label='GČP MSG Username' control={control} name='properties.gcpWs3Username' />
          <InputFormControl label='GČP MSG Password' control={control} name='properties.gcpWs3Pwd' />
          <InputFormControl label='GČP App Login' control={control} name='properties.gcpAppLogin' />
          <InputFormControl label='GČP Source App ID' control={control} name='properties.gcpSourceAppID' />
        </>}
      </Stack>
    </FormSection>
  }
)